import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Home, Loader2, Pencil, ClipboardList, Table as TableIcon } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AthleteFormDialog from "@/components/AthleteFormDialog";
import { SectionsView, SectionsEditor } from "@/components/SectionBlocks";
import { EditableImage } from "@/components/EditableImage";
import { EditableText } from "@/components/EditableText";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSiteLogo } from "@/lib/siteConfig";

const META = {
  artistica: { label: "Ginnastica Artistica", img: "https://images.pexels.com/photos/6572570/pexels-photo-6572570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tag: "Attrezzi, forza e precisione tecnica" },
  ritmica: { label: "Ginnastica Ritmica", img: "https://images.pexels.com/photos/15281444/pexels-photo-15281444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tag: "Nastro, cerchio, palla ed espressività" },
};

const stripHtml = (html) => { const d = document.createElement("div"); d.innerHTML = html || ""; return (d.textContent || "").trim(); };

async function urlToImage(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  const dataUrl = await new Promise((r, j) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.onerror = j; fr.readAsDataURL(blob); });
  const img = await new Promise((r, j) => { const im = new window.Image(); im.onload = () => r(im); im.onerror = j; im.src = dataUrl; });
  const fmt = dataUrl.substring(dataUrl.indexOf("/") + 1, dataUrl.indexOf(";")).toUpperCase();
  return { dataUrl, width: img.naturalWidth, height: img.naturalHeight, fmt: fmt === "JPG" ? "JPEG" : fmt };
}

export default function DisciplinePage() {
  const { discipline } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const logoUrl = useSiteLogo();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [events, setEvents] = useState([]);
  const [formOpen, setFormOpen] = useState(false);

  const valid = discipline === "artistica" || discipline === "ritmica";
  const meta = META[discipline];

  useEffect(() => {
    if (!valid) return;
    setLoading(true);
    Promise.all([api.get(`/discipline-pages/${discipline}`), api.get("/events")])
      .then(([c, e]) => {
        setSections(c.data.sections || []);
        setEvents((e.data || []).filter((ev) => ev.discipline === discipline));
      }).catch(() => {}).finally(() => setLoading(false));
  }, [discipline, valid]);

  if (!valid) return <Navigate to="/" replace />;

  const save = async (draft) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/discipline-pages/${discipline}`, { sections: draft });
      setSections(data.sections || draft);
      setEditMode(false);
      toast.success("Pagina salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const exportPDF = async () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = 196, marginX = 14; let y = 34;
    const now = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    let textX = 14;
    if (logoUrl) { try { const lg = await urlToImage(logoUrl); const hh = 18, w = Math.min(40, (lg.width / lg.height) * hh); doc.addImage(lg.dataUrl, lg.fmt, 14, 10, w, hh); textX = 14 + w + 5; } catch (e) {} }
    doc.setFontSize(18); doc.setTextColor(20, 20, 20); doc.text("OPES Ginnastiche", textX, 18);
    doc.setFontSize(13); doc.setTextColor(90, 90, 90); doc.text(meta.label, textX, 26);
    doc.setFontSize(9); doc.text(`Generato il ${now}`, textX, 32);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5); doc.line(14, y, 196, y); y += 8;
    const ensure = (need) => { if (y + need > 285) { doc.addPage(); y = 20; } };
    sections.forEach((sec) => {
      ensure(14); doc.setFontSize(14); doc.setTextColor(20, 20, 20); doc.text(sec.title || "", marginX, y); y += 7;
      (sec.blocks || []).forEach((b) => {
        if (b.type === "text") {
          const txt = stripHtml(b.html); if (!txt) return;
          doc.setFontSize(10); doc.setTextColor(60, 60, 60);
          doc.splitTextToSize(txt, pageW - 2).forEach((ln) => { ensure(6); doc.text(ln, marginX, y); y += 5; }); y += 3;
        } else if (b.type === "table" && (b.columns || []).length) {
          autoTable(doc, { startY: y, head: [b.columns], body: b.rows || [], theme: "grid", headStyles: { fillColor: [212, 175, 55], textColor: [2, 6, 23], fontStyle: "bold" }, styles: { fontSize: 9, cellPadding: 2 }, margin: { left: marginX, right: marginX } });
          y = doc.lastAutoTable.finalY + 6;
        }
      });
      y += 4;
    });
    doc.save(`${discipline}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF generato");
  };

  return (
    <div className="min-h-screen bg-background text-foreground noise-bg">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <SiteLogo className="w-9 h-9" />
            <div className="flex flex-col leading-tight">
              <BrandText />
            </div>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5"><Home className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      <section className="relative">
        <div className="relative h-72 sm:h-96 overflow-hidden border-b border-white/10">
          <EditableImage slot={`discipline_${discipline}`} defaultSrc={meta.img} alt={meta.label} className="absolute inset-0 w-full h-full" imgClassName="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/60 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 p-8 max-w-5xl mx-auto">
            <span className="overline text-gold uppercase text-xs tracking-[0.25em]"><EditableText slot={`disc_${discipline}_overline`} defaultText="Disciplina" /></span>
            <h1 data-testid="discipline-title" className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2"><EditableText slot={`disc_${discipline}_title`} defaultText={meta.label} /></h1>
            <p className="text-muted-foreground mt-2"><EditableText slot={`disc_${discipline}_tag`} defaultText={meta.tag} /></p>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : (
        <main className="max-w-5xl mx-auto px-6 py-14 space-y-12">
          {isAdmin && !editMode && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button data-testid="export-page-pdf" variant="outline" size="sm" onClick={exportPDF} className="border-white/15 hover:border-gold/60"><TableIcon className="w-3.5 h-3.5 mr-1.5" /> Esporta PDF</Button>
              <Button data-testid="edit-page-btn" size="sm" onClick={() => setEditMode(true)} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifica pagina</Button>
            </div>
          )}

          <section data-testid="section-iscrizione-legacy" className="hidden" />

          {editMode ? (
            <SectionsEditor initial={sections} onSave={save} onCancel={() => setEditMode(false)} saving={saving} onRegister={() => setFormOpen(true)} />
          ) : (
            <SectionsView sections={sections} onRegister={() => setFormOpen(true)} />
          )}
        </main>
      )}

      <AthleteFormDialog open={formOpen} onOpenChange={setFormOpen} discipline={discipline} events={events} editing={null} onSaved={() => setFormOpen(false)} />
    </div>
  );
}
